/* global SideDrawer */

if (!customElements.get('search-drawer')) {
  class SearchDrawer extends SideDrawer {
    constructor() {
      super();

      this.searchInput = this.querySelector('.js-search-input');
      this.closeBtn = this.querySelector('.drawer__close-btn');
      this.bindEvents();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.docClickHandler);
      document.removeEventListener('pointerdown', this.docPointerdownHandler);
      document.removeEventListener('focusin', this.docFocusinHandler);

      if (Shopify.designMode) {
        document.removeEventListener('shopify:section:select', this.sectionSelectHandler);
        document.removeEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }
    }

    bindEvents() {
      // Triggers are bound with document-level delegation: the header section
      // is re-rendered by the Theme Editor on every setting change (and the
      // sticky header can re-create its icons), which would orphan listeners
      // bound directly to the elements.
      //
      // Covered triggers:
      // - the minimised search icon(s) (.js-open-search-drawer)
      // - the visible header search bar (.header__search .search). It must
      //   open on 'click' (not 'pointerdown'): opening earlier makes the
      //   trailing click land on the page overlay, which closes the drawer
      //   again. 'pointerdown' only prevents the header input from focusing,
      //   so the keyboard never opens on it. 'focusin' covers Tab access.
      // On desktop the drawer opens with the search input focused, ready for
      // typing. On mobile it opens WITHOUT focusing it: trapFocus falls back
      // to the drawer container, so the keyboard (and the iOS zoom-in on the
      // field) never triggers on open — the user taps the field when they
      // want to type.
      this.docClickHandler = (evt) => {
        const trigger = evt.target.closest('.js-open-search-drawer, .header__search .search');
        if (!trigger) return;
        evt.preventDefault();
        this.open(trigger, this.autofocusTarget());
      };

      this.docPointerdownHandler = (evt) => {
        if (evt.target.closest('.header__search .search')) evt.preventDefault();
      };

      this.docFocusinHandler = (evt) => {
        if (!evt.target.closest('.header__search .search')) return;
        if (this.getAttribute('aria-hidden') === 'false') return;
        evt.target.blur();
        this.open(evt.target, this.autofocusTarget());
      };

      document.addEventListener('click', this.docClickHandler);
      document.addEventListener('pointerdown', this.docPointerdownHandler);
      document.addEventListener('focusin', this.docFocusinHandler);

      // While the input has text, the drawer close button acts as "clear"
      // instead (the native .search__reset is hidden via CSS, so there are
      // never two X buttons side by side). With an empty input the click
      // bubbles up to SideDrawer's own handler, which closes the drawer.
      if (this.closeBtn && this.searchInput) {
        this.closeBtn.addEventListener('click', (evt) => {
          if (!this.searchInput.value.length) return;
          evt.stopPropagation();
          this.searchInput.value = '';
          this.searchInput.dispatchEvent(new Event('input', { bubbles: true }));
          this.searchInput.focus();
        });

        this.searchInput.addEventListener('input', this.updateCloseBtnLabel.bind(this));
      }

      if (Shopify.designMode) {
        this.sectionSelectHandler = this.handleSectionSelect.bind(this);
        this.sectionDeselectHandler = this.handleSectionDeselect.bind(this);
        document.addEventListener('shopify:section:select', this.sectionSelectHandler);
        document.addEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }
    }

    /**
     * Element to focus when the drawer opens: the search input on desktop,
     * none on mobile (trapFocus then falls back to the drawer container).
     * @returns {Element|undefined}
     */
    autofocusTarget() {
      return window.theme && theme.mediaMatches && theme.mediaMatches.md ? this.searchInput : undefined;
    }

    /**
     * Keeps the close button's screen-reader label in sync with its current
     * role ("Close" with an empty input, "Reset" while it acts as clear).
     */
    updateCloseBtnLabel() {
      const label = this.closeBtn.querySelector('.visually-hidden');
      if (!label) return;
      label.textContent = this.searchInput.value.length
        ? this.closeBtn.dataset.labelClear
        : this.closeBtn.dataset.labelClose;
    }

    /**
     * Handle when the section is selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionSelect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.open();
    }

    /**
     * Handle when the section is de-selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionDeselect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.close();
    }
  }

  customElements.define('search-drawer', SearchDrawer);
}
